import MarketoForm from 'molecules/Form';
import Icon from 'molecules/Icon';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import Reveal from 'components/Animations/Reveal';
import WistiaFormEmbed from 'components/WistiaForm/WistiaFormEmbed';
import { useWistiaFormData } from 'components/WistiaForm/utils/useWistiaFormData';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';
import ClientOnly from 'utils/hooks/useClientOnly';
import HeadingHighlights from 'utils/strings/HeadingHighlights';

import type { EventRegistrationHeroProps } from 'components/EventRegistrationHero/config';
import type { FC } from 'react';

type MarketoRef = { model?: string; id?: string; value?: { data?: FormReference } };

const EventRegistrationHero: FC<EventRegistrationHeroProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const {
    eyebrow,
    heading,
    body,
    calloutLabel,
    eventDate,
    eventTime,
    formTool = 'marketo',
    formHeadline,
    formLayout = 'grid',
    marketoForm,
    wistiaForm,
    redirectUrl,
  } = rest;

  // Gated fetch: skip the client-side Builder call when the form is SSR-preloaded.
  const typedMarketo = marketoForm as MarketoRef | undefined;
  const hasPreloadedMarketo = !!typedMarketo?.value?.data;
  const marketoRefData = useBuilderReference(
    !hasPreloadedMarketo ? typedMarketo?.model : undefined,
    !hasPreloadedMarketo ? typedMarketo?.id : undefined,
  );
  const marketoData = (hasPreloadedMarketo ? typedMarketo?.value?.data : marketoRefData) as FormReference | undefined;

  const { formId, embedType } = useWistiaFormData(wistiaForm);

  const isWistia = formTool === 'wistia';
  const hasCallout = Boolean(calloutLabel || eventDate || eventTime);

  return (
    <Section {...sectionProps} bare>
      <div aria-hidden className="bg-event-hero-fade pointer-events-none absolute inset-x-0 bottom-0 h-64" />

      <div className="relative z-10 mx-auto grid max-w-screen-xl grid-cols-1 items-start gap-12 px-4 sm:px-16 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal mount className="flex flex-col gap-6">
          {eyebrow && (
            <span className="font-spaceGrotesk text-sm font-semibold uppercase tracking-widest text-primary-300">
              {eyebrow}
            </span>
          )}

          {heading && (
            <h1 className="font-spaceGrotesk text-display-md font-semibold leading-tight text-white sm:text-display-lg">
              <HeadingHighlights heading={heading} />
            </h1>
          )}

          {body && <p className="max-w-xl text-lg leading-8 text-gray-300">{body}</p>}

          {hasCallout && (
            <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-primary-1100 bg-black/20 px-6 py-5">
              {calloutLabel && <span className="text-md text-primary-300">{calloutLabel}</span>}
              {eventDate && (
                <span className="flex items-center gap-2 text-gray-300">
                  <Icon icon="calendar" size={20} className="text-primary-300" />
                  <span className="text-md">{eventDate}</span>
                </span>
              )}
              {eventTime && (
                <span className="flex items-center gap-2 text-gray-300">
                  <Icon icon="clock" size={20} className="text-primary-300" />
                  <span className="text-md">{eventTime}</span>
                </span>
              )}
            </div>
          )}
        </Reveal>

        {/* Form renders its own card and title; no wrapper (would nest). formHeadline is Wistia-only. */}
        <div className="relative z-10 flex flex-col gap-4">
          {isWistia && (
            <>
              {formHeadline && <h2 className="font-spaceGrotesk text-xl font-semibold text-white">{formHeadline}</h2>}
              <WistiaFormEmbed
                formId={formId}
                embedType={embedType}
                redirectUrl={redirectUrl}
                layout={formLayout as 'grid' | 'inline'}
              />
            </>
          )}

          {!isWistia && marketoData && (
            <ClientOnly>
              <div className={formLayout === 'inline' ? 'event-form-inline' : undefined}>
                <MarketoForm formData={marketoData as FormReference} />
              </div>
            </ClientOnly>
          )}

          {!isWistia && !marketoData && (
            <div className="flex items-center justify-center rounded-lg bg-gray-800/40 px-6 py-10 text-center text-gray-400">
              Select a Marketo form in the Builder editor to display it.
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default EventRegistrationHero;
